<script setup lang="ts">
import { onMounted, ref } from "vue";
import { bemBuilder, toMoney } from "@chatfood/core-utils";
import {
  useToast,
  AtomText,
  AtomLoadingSpinner,
  SatIconButton,
  SatElasticPanel,
} from "@chatfood/design-system";
import { ApplyGiftCard } from "../apply-gift-card";
import { useLocale } from "~/composables/use-locale";
import { BASE_TELEPORT_SELECTOR } from "~/domain/base-teleport-selector";
import { useBasketStore } from "~/stores/basket";
import { useGiftCardStore } from "~/stores/giftCard";
import { useBugReporter } from "#imports";

const css = bemBuilder("gift-card-view");

const { t } = useLocale();
const { report } = useBugReporter();

const basket = useBasketStore();
const giftCard = useGiftCardStore();

const { toastError } = useToast();

const showModal = ref(false);
const isDeletingGiftCard = ref(false);

const toggleModal = () => {
  showModal.value = !showModal.value;
};

const onRemoveGiftCard = async () => {
  isDeletingGiftCard.value = true;
  try {
    await basket.removeGiftCard();
  } catch (e) {
    report(e);
    toastError(t("components.gift_card_view.remove_error"));
  } finally {
    isDeletingGiftCard.value = false;
  }
};

onMounted(() => {
  giftCard.loadProviders(basket.outletId, basket.salesChannel);
});
</script>

<template>
  <div
    v-if="giftCard.providers.length || basket.giftCard"
    :class="css()"
    data-test="gift-card-view"
  >
    <AtomText type="subtitle">
      {{ t("components.gift_card_view.title") }}
    </AtomText>
    <div :class="css('box')">
      <a
        v-if="!basket.giftCard"
        href=""
        :class="css('open-selection')"
        data-test="cta-open-apply-gift-card"
        @click.prevent="toggleModal"
      >
        <AtomText type="body-bold" color="color-primary">
          {{ t("components.gift_card_view.gift_card_cta") }}
        </AtomText>
      </a>
      <div v-else :class="css('applied')" data-test="gift-card-applied">
        <AtomText>
          {{
            t("components.gift_card_view.applied_amount", {
              amount: toMoney(
                basket.giftCard.amount.value,
                basket.giftCard.amount.currency,
              ),
            })
          }}
        </AtomText>
        <SatIconButton
          v-if="!isDeletingGiftCard"
          variant="danger"
          theme="ghost"
          data-test="delete-cta"
          icon-name="delete"
          size="extra-small"
          @click="onRemoveGiftCard"
        />
        <AtomLoadingSpinner v-else :size="21" />
      </div>
    </div>
    <teleport :to="BASE_TELEPORT_SELECTOR">
      <SatElasticPanel v-model="showModal" show-close width-fit-content>
        <ApplyGiftCard :class="css('selection')" @close="toggleModal" />
      </SatElasticPanel>
    </teleport>
  </div>
</template>

<style lang="scss" scoped>
.gift-card-view {
  display: flex;
  gap: var(--spacing-md);
  padding-top: var(--spacing-md);
  padding-inline: var(--spacing-lg);
  margin-inline: calc(var(--spacing-lg) * -1);
  flex-direction: column;
  border-top: 1px solid var(--color-base-95);

  &__box {
    width: 100%;
    min-height: 51px;
    border-radius: var(--radii-card-sm);
    border: 1px solid var(--color-base-90);
    background: var(--color-base-100);
  }

  &__open-selection {
    display: block;
    padding: var(--spacing-md);
  }

  &__applied {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    gap: var(--spacing-sm);
    padding: var(--spacing-md);
  }

  &__selection {
    max-width: 100%;

    @media (width > $breakpoint-sm) {
      width: 500px;
    }
  }
}
</style>
